import { createAsyncThunk, createEntityAdapter, createSelector, createSlice, EntityState, PayloadAction } from '@reduxjs/toolkit'
import { IntegrationConfig, IntegrationConfigCreationPayload } from '@sendhub/integrations/api-client-typescript-axios'
import { isAxiosError } from 'axios'
import lodash from 'lodash'
import { integrationsApi } from '../../services/integrations-api'
import { RootState } from '../root'

export const INTEGRATION_CONFIGS_FEATURE_KEY = 'integrationConfigs'

export interface IntegrationConfigsState extends EntityState<Readonly<IntegrationConfig>> {
  loadingStatus: 'not loaded' | 'loading' | 'loaded' | 'error'
  error?: string | null
}

export const integrationConfigsAdapter = createEntityAdapter<Readonly<IntegrationConfig>>()

/**
 * Export an effect using createAsyncThunk from
 * the Redux Toolkit: https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(fetchIntegrationConfigs())
 * }, [dispatch]);
 * ```
 */
export const fetchIntegrationConfigs = createAsyncThunk('integrationConfigs/fetchStatus', async (_, thunkAPI) => {
  return integrationsApi.listIntegrationConfigs().then((r) => r.data)
})
export const createIntegrationConfig = createAsyncThunk(
  'integrationConfigs/createStatus',
  async (integrationConfig: IntegrationConfigCreationPayload, thunkAPI) => {
    return integrationsApi.createIntegrationConfig(integrationConfig).then((r) => thunkAPI.dispatch(fetchIntegrationConfigs()))
  }
)
/** Structured error surfaced to forms when updating an integration config fails. */
export interface ConfigUpdateError {
  message: string
  code?: string
  /** Form field this error pertains to, if any (e.g. 'serviceAccountId'). */
  field?: string
}

export const updateIntegrationConfig = createAsyncThunk(
  'integrationConfigs/updateStatus',
  async ({ id, integrationConfig }: { id: string; integrationConfig: IntegrationConfigCreationPayload }, thunkAPI) => {
    try {
      await integrationsApi.updateIntegrationConfig(id, integrationConfig)
      await thunkAPI.dispatch(fetchIntegrationConfigs())
      return undefined
    } catch (error) {
      if (isAxiosError(error) && error.response?.data) {
        const data = error.response.data
        const field = (data.field as string | undefined) ?? (data.errors?.[0]?.path as string | undefined)?.replace('.body.', '')
        return thunkAPI.rejectWithValue({
          message: (data.message as string | undefined) ?? 'Failed to save integration settings. Please try again.',
          code: data.code as string | undefined,
          field,
        } as ConfigUpdateError)
      }
      // No response (network error, unreachable server, bare proxy 502, etc.)
      return thunkAPI.rejectWithValue({
        message: 'Unable to reach SendHub. Please check your connection and try again.',
        code: 'network_error',
      } as ConfigUpdateError)
    }
  }
)

export const initialIntegrationConfigsState: IntegrationConfigsState = integrationConfigsAdapter.getInitialState({
  loadingStatus: 'not loaded',
  error: null,
})

export const integrationConfigsSlice = createSlice({
  name: INTEGRATION_CONFIGS_FEATURE_KEY,
  initialState: initialIntegrationConfigsState,
  reducers: {
    add: integrationConfigsAdapter.addOne,
    setMany: integrationConfigsAdapter.setMany,
    remove: integrationConfigsAdapter.removeOne,
    // ...
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchIntegrationConfigs.pending, (state: IntegrationConfigsState) => {
        state.loadingStatus = 'loading'
      })
      .addCase(fetchIntegrationConfigs.fulfilled, (state: IntegrationConfigsState, action: PayloadAction<IntegrationConfig[]>) => {
        integrationConfigsAdapter.setAll(state, action.payload)
        state.loadingStatus = 'loaded'
      })
      .addCase(fetchIntegrationConfigs.rejected, (state: IntegrationConfigsState, action) => {
        state.loadingStatus = 'error'
        state.error = action.error.message
      })
  },
})

/*
 * Export reducer for store configuration.
 */
export const integrationConfigsReducer = integrationConfigsSlice.reducer

/*
 * Export action creators to be dispatched. For use with the `useDispatch` hook.
 *
 * e.g.
 * ```
 * import React, { useEffect } from 'react';
 * import { useDispatch } from 'react-redux';
 *
 * // ...
 *
 * const dispatch = useDispatch();
 * useEffect(() => {
 *   dispatch(integrationConfigsActions.add({ id: 1 }))
 * }, [dispatch]);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#usedispatch
 */
export const integrationConfigsActions = integrationConfigsSlice.actions

/*
 * Export selectors to query state. For use with the `useSelector` hook.
 *
 * e.g.
 * ```
 * import { useSelector } from 'react-redux';
 *
 * // ...
 *
 * const entities = useSelector(selectAllIntegrationConfigs);
 * ```
 *
 * See: https://react-redux.js.org/next/api/hooks#useselector
 */
const { selectAll, selectEntities } = integrationConfigsAdapter.getSelectors()

export const getIntegrationConfigsState = (rootState: RootState): IntegrationConfigsState => rootState[INTEGRATION_CONFIGS_FEATURE_KEY]

export const selectAllIntegrationConfigs = createSelector(getIntegrationConfigsState, selectAll)

export const selectIntegrationConfigsEntities = createSelector(getIntegrationConfigsState, selectEntities)
export const selectIntegrationConfigsLoadingStatus = createSelector(getIntegrationConfigsState, (state) => state.loadingStatus)
export const selectIntegrationConfigsError = createSelector(getIntegrationConfigsState, (state) => state.error)
